import { motion, useMotionValue, useSpring, useReducedMotion } from "motion/react";
import { ReactNode, useRef, MouseEvent } from "react";

interface Props {
  children: ReactNode;
  className?: string;
  /** Pull strength 0–1. Default 0.25. */
  strength?: number;
  /** Activation radius in px. Default 80. */
  radius?: number;
  onClick?: () => void;
  type?: "button" | "submit";
  ariaLabel?: string;
}

/**
 * Cursor-attracted wrapper. Subtle pull within radius, springs back on leave.
 * Falls back to a plain button when prefers-reduced-motion.
 */
const MagneticButton = ({
  children,
  className,
  strength = 0.25,
  radius = 80,
  onClick,
  type = "button",
  ariaLabel,
}: Props) => {
  const ref = useRef<HTMLButtonElement>(null);
  const reduce = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 18, mass: 0.6 });
  const sy = useSpring(y, { stiffness: 200, damping: 18, mass: 0.6 });

  const handleMove = (e: MouseEvent<HTMLButtonElement>) => {
    if (reduce || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > radius) {
      x.set(0);
      y.set(0);
      return;
    }
    x.set(dx * strength);
    y.set(dy * strength);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.button
      ref={ref}
      type={type}
      onClick={onClick}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={reduce ? undefined : { x: sx, y: sy }}
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </motion.button>
  );
};

export default MagneticButton;
